import { Link, useParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { INDUSTRIES, PRODUCTS, CATEGORIES } from "../lib/data";
import { PageHero, SectionHead, CTABand } from "../components/site/Bits";
import { Reveal } from "../components/site/Reveal";

import { Seo } from "../components/site/Seo";

export default function IndustryDetail() {
  const { slug } = useParams();
  const ind = INDUSTRIES.find((i) => i.slug === slug);

  if (!ind) {
    return (
      <div className="pt-40 pb-40 text-center container-x">
        <h1 className="font-display font-bold text-3xl text-ink">Sector not found</h1>
        <Link to="/industries" className="mt-4 inline-block text-flame font-semibold">Back to sectors</Link>
      </div>
    );
  }

  const sectorProducts = PRODUCTS.filter((p) => p.sectors.includes(slug));
  const sectorCategories = CATEGORIES.filter((c) => sectorProducts.some((p) => p.categorySlug === c.slug));
  const gpCategory = CATEGORIES.find((c) => c.slug === "ground-protection");
  const hasGroundProtection = sectorProducts.some((p) => p.categorySlug === "ground-protection");
  // Ground protection is sold as "any mat" (not a single product) — exclude individual GP products here.
  const displayProducts = sectorProducts.filter((p) => p.categorySlug !== "ground-protection");

  return (
    <div data-testid="industry-detail-page">
      <Seo title={ind.name} description={ind.blurb} image={ind.image} />
      <PageHero
        kicker="Sector Solutions"
        title={ind.name}
        subtitle={ind.blurb}
        image={ind.image}
      />

      {/* Use cases + product categories */}
      <section className="bg-background">
        <div className="container-x py-24 grid gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <SectionHead kicker="Common Use Cases" title={`How we support ${ind.name}`} desc="We start by understanding how your crews work — then match the right products and solutions to each use case." />
            <div className="mt-8 space-y-4">
              {ind.useCases.map((u) => (
                <div key={u} className="group flex items-center gap-4 border border-border bg-card p-5 overflow-hidden hover:bg-ink hover:border-ink hover:-translate-y-1 hover:shadow-2xl transition-[transform,box-shadow,background-color,border-color] duration-500 ease-[cubic-bezier(0.25,1,0.5,1)]">
                  <div className="h-10 w-10 flex-shrink-0 bg-flame/10 border border-flame/25 text-flame flex items-center justify-center group-hover:bg-flame group-hover:text-white group-hover:border-flame transition-colors duration-500">
                    <CheckCircle2 className="h-5 w-5" />
                  </div>
                  <span className="text-charcoal font-medium group-hover:text-white transition-colors duration-500">{u}</span>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="border border-border bg-ink text-white p-8">
              <div className="kicker text-flame">Product Categories for this Sector</div>
              <h3 className="mt-3 font-display font-bold text-2xl">Matched to your work</h3>
              <p className="mt-3 text-white/70 text-sm leading-relaxed">
                These categories serve {ind.name} today — and each one grows as we add proven products.
              </p>
              <div className="mt-6 space-y-2">
                {sectorCategories.map((c) => {
                  const Icon = Icons[c.icon] || Icons.Package;
                  return (
                    <Link key={c.slug} to={c.to} className="flex items-center gap-3 border border-white/10 px-4 py-3 hover:border-flame/50 transition-colors">
                      <Icon className="h-5 w-5 text-flame flex-shrink-0" />
                      <span className="font-display font-semibold flex-1">{c.name}</span>
                      <ArrowRight className="h-4 w-4 text-flame" />
                    </Link>
                  );
                })}
              </div>
              <Link to="/request-quote" data-testid="industry-quote-cta" className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-flame text-white px-6 py-3.5 font-semibold hover:bg-flame/90 transition-colors">
                Request a Quote <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Relevant products */}
      <section className="bg-charcoal">
        <div className="container-x py-24">
          <SectionHead dark kicker="Recommended Products" title={`Solutions for ${ind.name}`} desc="Curated products that fit this sector's use cases — with more added over time." />
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {displayProducts.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.1}>
                <Link to={`/products/${p.slug}`} className="group relative block bg-ink border border-white/10 overflow-hidden hover:border-flame/60 hover:-translate-y-1.5 hover:shadow-2xl transition-[transform,box-shadow,border-color] duration-300 ease-[cubic-bezier(0.25,1,0.5,1)] h-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flame focus-visible:ring-offset-2 focus-visible:ring-offset-charcoal">
                  <span aria-hidden="true" className="absolute top-0 left-0 z-30 h-full w-1 bg-flame origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-500 ease-out" />
                  <div className="relative aspect-[4/3] overflow-hidden bg-black">
                    <img src={p.image} alt={p.name} className={`h-full w-full group-hover:scale-110 transition-transform duration-700 ease-[cubic-bezier(0.25,1,0.5,1)] ${p.fit === "contain" ? "object-contain bg-white p-3" : "object-cover"}`} />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>
                  <div className="p-6">
                    <div className="kicker text-flame">{p.category}</div>
                    <h3 className="mt-2 font-display font-bold text-xl text-white group-hover:text-flame transition-colors">{p.name}</h3>
                    <p className="mt-2 text-sm text-white/60 leading-relaxed">{p.tagline}</p>
                  </div>
                </Link>
              </Reveal>
            ))}

            {hasGroundProtection && gpCategory && (
              <Reveal delay={displayProducts.length * 0.1}>
                <Link to="/ground-protection" data-testid="sector-ground-protection-card" className="group relative block bg-ink border border-white/10 overflow-hidden hover:border-flame/60 hover:-translate-y-1.5 hover:shadow-2xl transition-[transform,box-shadow,border-color] duration-300 ease-[cubic-bezier(0.25,1,0.5,1)] h-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flame focus-visible:ring-offset-2 focus-visible:ring-offset-charcoal">
                  <span aria-hidden="true" className="absolute top-0 left-0 z-30 h-full w-1 bg-flame origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-500 ease-out" />
                  <div className="relative aspect-[4/3] overflow-hidden bg-black">
                    <img src={gpCategory.image} alt="Ground protection matting" className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-700 ease-[cubic-bezier(0.25,1,0.5,1)]" />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
                    <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-flame text-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest">Single Source</span>
                  </div>
                  <div className="p-6">
                    <div className="kicker text-flame">Ground Protection & Site Access</div>
                    <h3 className="mt-2 font-display font-bold text-xl text-white group-hover:text-flame transition-colors">Any Mat You Need</h3>
                    <p className="mt-2 text-sm text-white/60 leading-relaxed">We source any ground-protection or site-access mat that fits your project — buy or rent. One source for every mat type.</p>
                    <span className="mt-4 inline-flex items-center gap-2 text-flame text-sm font-semibold">Explore ground protection <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></span>
                  </div>
                </Link>
              </Reveal>
            )}
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
